import { FhirInstance } from '../config/FhirClient';
import { IBundle } from '../modules/unsw-fhir/Bundle';
import {
  IOperationOutcome,
  isOperationOutcome,
} from '../modules/unsw-fhir/OperationOutcome';
import { IQuestionnaireResponse } from '../modules/unsw-fhir/QuestionnaireResponse';
import { isQuestionnaireResponse } from '../modules/unsw-fhir/QuestionnaireResponse/typeGuards';

export const getQuestionnaireResponseByReference = async (
  patientId: string,
  questionnaireId: string,
) => {
  if (!patientId || !questionnaireId) return undefined;

  const response = await FhirInstance.get<IBundle | IOperationOutcome>(
    '/QuestionnaireResponse',
    {
      params: {
        questionnaire: questionnaireId,
        patient: patientId,
      },
    },
  );
  const { data } = response;
  if (isOperationOutcome(data)) {
    throw new Error(data.text || 'Request failed');
  }

  if (!data.entry) {
    return undefined;
  }

  return data.entry
    .filter(entry => {
      return !!entry.resource && isQuestionnaireResponse(entry.resource);
    })
    .map(
      (entry): IQuestionnaireResponse =>
        entry.resource as IQuestionnaireResponse,
    )[0];
};

export const getQuestionnaireResponsesByPatientId = async (
  patientId: string,
) => {
  if (!patientId) return [];

  const response = await FhirInstance.get<IBundle | IOperationOutcome>(
    '/QuestionnaireResponse',
    {
      params: {
        patient: patientId,
      },
    },
  );
  const { data } = response;
  if (isOperationOutcome(data)) {
    throw new Error(data.text || 'Request failed');
  }

  if (!data.entry) {
    return [];
  }

  return data.entry
    .filter(entry => {
      return !!entry.resource && isQuestionnaireResponse(entry.resource);
    })
    .map(
      (entry): IQuestionnaireResponse =>
        entry.resource as IQuestionnaireResponse,
    );
};

export const getQuestionnaireResponseById = async (id: string) => {
  const response = await FhirInstance.get<
    IQuestionnaireResponse | IOperationOutcome
  >(`/QuestionnaireResponse/${id}`);
  const { data } = response;
  if (isOperationOutcome(data)) {
    throw new Error(data.text || 'Request failed');
  }

  if (!data) {
    return undefined;
  }

  return data;
};

export const putQuestionnaireResponse = async (
  id: string,
  questionnaireResponse: IQuestionnaireResponse,
) => {
  const response = await FhirInstance.put<
    IQuestionnaireResponse | IOperationOutcome
  >(`/QuestionnaireResponse/${id}`, questionnaireResponse);
  const { data } = response;
  if (isOperationOutcome(data)) {
    throw new Error(data.text || 'Request failed');
  }

  return data;
};

export const postQuestionnaireResponse = async (
  questionnaireResponse?: IQuestionnaireResponse,
) => {
  if (!questionnaireResponse) return undefined;
  const response = await FhirInstance.post<
    IQuestionnaireResponse | IOperationOutcome
  >('/QuestionnaireResponse', questionnaireResponse);
  const { data } = response;
  if (isOperationOutcome(data)) {
    throw new Error(data.text || 'Request failed');
  }

  return data;
};

export const deleteQuestionnaireResponseById = async (id: string) => {
  try {
    const response = await FhirInstance.delete<
      IQuestionnaireResponse | IOperationOutcome
    >(`/QuestionnaireResponse/${id}`);
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const getQuestionnaireResponses = async (count?: number) => {
  try {
    let queryStr = '';
    if (count) {
      const params = { _count: count.toString(10) };
      queryStr = new URLSearchParams(params).toString();
    }

    const response = await FhirInstance.get<IBundle | IOperationOutcome>(
      '/QuestionnaireResponse' + queryStr,
    );
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      return [];
    }

    return data.entry
      .filter(entry => {
        return !!entry.resource && isQuestionnaireResponse(entry.resource);
      })
      .map(
        (entry): IQuestionnaireResponse =>
          entry.resource as IQuestionnaireResponse,
      );
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getNumQuestionnaireResponses = async () => {
  try {
    const response = await FhirInstance.get<IBundle | IOperationOutcome>(
      '/QuestionnaireResponse?_summary=count',
    );
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      throw new Error('unable to get patient count');
    }

    return data.total;
  } catch (e) {
    console.error(e);
  }
};

export const getAllQuestionnaireResponses = async () => {
  const count = await getNumQuestionnaireResponses();
  return await getQuestionnaireResponses(count);
};
